import React, { useContext, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { InfluencerDataProvider } from './contexts/InfluencerDataContext';
import { CollabDataProvider } from './contexts/collabDataContext';
import { InfluencerCampaignDataProvider } from './contexts/InfluencerCampaignDataContext';
import { ClientDataProvider } from 'contexts/ClientDataContext';
import NewLoginPage from 'features/authentication/NewLogin';
import ReturningUser from 'features/authentication/ReturningUser';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import { isSignInWithEmailLink } from 'firebase/auth';
import Verification from 'features/authentication/Verification';
import { VerificationProvider } from 'contexts/PhoneVerificationContext';
import RecaptchaInitializer from 'features/collabs/components/RecaptchaInitializer';
import RedeemRewardPage from './features/collabs/pages/RedeemRewardPage';
import ViewRewardPage from './features/collabs/pages/ViewRewardPage';
import SignUpPage from 'features/authentication/NewSignup';
import EmailSentPage from 'features/authentication/EmailSentPage';
import DeliverableSubmissionPage from 'features/collabs/pages/DeliverableSubmissionPage';
import MainLayout from './components/MainLayout';
import { TabBarVisibilityProvider } from './contexts/TabBarVisibilityContext';
import ReviewCollabPage from './features/collabs/pages/ReviewCollabPage';
import ScheduleCollabPage from './features/collabs/scheduling';
import CounterCollabPage from './features/collabs/pages/CounterCollabPage';
import ClientPage from './features/explore/pages/ClientPage';
import SignUpApplicationPage from 'features/authentication/SignUpApplicationPage';
import InstagramVerificationPage from 'features/authentication/InstagramVerificationPage';
import RequestCollabPage from 'features/explore/pages/RequestCollabPage';
import CampaignDetailPage from 'features/explore/pages/CampaignDetailPage';
import ExplorePage from './features/explore';
import { WhiteLabelContext } from './contexts/WhiteLabelContext';
import AuthRouteGuard from './components/AuthRouteGuard';
import Inbox from 'features/inbox';
import MessagesPage from 'features/inbox/components/messagesPage';
import { ConversationsProvider } from './contexts/ConversationsDataContext';
import { MarketplaceOrdersProvider } from './contexts/MarketplaceOrdersContext';
import { InstagramVerificationProvider } from 'contexts/InstagramVerificationContext';
import ProfilePage from 'features/profile';
import ScheduleRetailCollabPage from './features/collabs/retail/ScheduleRetailCollabPage';
import SelectProductsPage from './features/collabs/products/SelectProductsPage';
import { InfluencerLocationProvider } from 'contexts/InfluencerLocationContext';
import { AssociatedTagsProvider } from 'contexts/AssociatedTagsContext';
import { AffiliateDetails } from './features/profile/components/AffiliateDetails';
import UgcSubmissionPage from 'features/collabs/pages/UgcSubmissionPage';
import ContentGuidelinesPage from 'features/collabs/pages/ContentGuidelinesPage';
import CollabDetailsPage from 'features/collabs/pages/CollabDetailsPage';
import HomePage from 'features/collabs/pages/HomePage';
import ContentUploadPage from './features/collabs/pages/ContentUploadPage';
import PendingShipmentPage from 'features/collabs/pages/PendingShipmentPage';
import CollabInviteRedirect from './components/CollabInviteRedirect';
import MarketplaceOrderAcceptPage from 'features/collabs/pages/MarketplaceOrderAcceptPage';
import MarketplaceOrderSubmissionPage from 'features/collabs/pages/MarketplaceOrderSubmissionPage';
import MarketplaceOrderUploadContentPage from 'features/collabs/pages/MarketplaceOrderUploadContentPage';
import CounterMarketplaceOrderPage from 'features/collabs/pages/CounterMarketplaceOrderPage';
import BountyDataProvider from './contexts/BountyDataContext';
import BountiesPage from 'features/bounties';
import ProposeBountyPage from 'features/bounties/pages/ProposeBountyPage';
import ProposalSubmittedPage from 'features/bounties/pages/ProposalSubmittedPage';
import ResubmitProposalPage from 'features/bounties/pages/ResubmitProposalPage';
import BountyClaimFlowPage from 'features/bounties/pages/BountyClaimFlowPage';

// Lazy load wallet to avoid Whop script/Server Components errors during initial load
const WalletPage = lazy(() => import('features/wallet'));

const App: React.FC = () => {
  const [user] = useAuthState(auth);
  const isEmailSignInLink = isSignInWithEmailLink(auth, window.location.href);
  const { isWhiteLabeled } = useContext(WhiteLabelContext);

  return (
    <>
      <RecaptchaInitializer />
        <InfluencerDataProvider>
          <InfluencerLocationProvider>
          <CollabDataProvider>
            <InfluencerCampaignDataProvider> 
              <ClientDataProvider>
              <BountyDataProvider>
                <InstagramVerificationProvider>
                <VerificationProvider>
                  <MarketplaceOrdersProvider>
                  <ConversationsProvider>
                  <Routes>                    
                    {/* Auth routes - accessible regardless of white label */}
                    <Route path="login" element={!user ? <NewLoginPage /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    <Route path="new-signup" element={!user ? <SignUpPage /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    <Route path="signup-application" element={!user ? <SignUpApplicationPage /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    <Route path="instagram-verification" element={<InstagramVerificationPage />} />
                    <Route path="returning-user" element={!user ? <ReturningUser /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    <Route path="verification" element={!user ? <Verification /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    <Route path="email-sent" element={!user ? <EmailSentPage /> : <Navigate to={isWhiteLabeled ? "/" : "/collabs"} replace />} />
                    
                    {/* Collab invite route - accessible regardless of authentication */}
                    <Route path="collab-invite" element={<CollabInviteRedirect />} />

                    {/* White labeled client routes */}
                    {isWhiteLabeled ? (
                      <>
                        <Route path="/" element={<MainLayout><ClientPage /></MainLayout>} />
                        <Route path="request-collab" element={<RequestCollabPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </>
                    ) : (
                      <>
                        {/* Main app routes - protected by auth guard */}
                        <Route element={
                          <AuthRouteGuard>
                            <TabBarVisibilityProvider>
                              <MainLayout>
                                <Outlet />
                              </MainLayout>
                            </TabBarVisibilityProvider>
                          </AuthRouteGuard>
                        }>
                          <Route path="/" element={user ? <Navigate to="/collabs" replace /> : <Navigate to="/login" replace />} />
                          <Route path="profile" element={
                            <AssociatedTagsProvider>
                              <Outlet />
                            </AssociatedTagsProvider>
                          }>
                            <Route index element={<ProfilePage />} />
                            <Route path="affiliate-details" element={<AffiliateDetails />} />
                          </Route>
                          <Route path="collabs" element={<Outlet />}>
                            <Route index element={<HomePage isFirstTime={!user && isEmailSignInLink} />} />
                            <Route path="review-collab/:id" element={<ReviewCollabPage />} />
                            <Route path="redeem-reward" element={<RedeemRewardPage />} />
                            <Route path="view-reward" element={<ViewRewardPage />} />
                            <Route path="schedule-collab" element={<ScheduleCollabPage />} />
                            <Route path="content-submission" element={<UgcSubmissionPage />} />
                            <Route path="complete-collab" element={<DeliverableSubmissionPage />} />
                            <Route path="counter-collab" element={<CounterCollabPage />} />
                            <Route path="request-collab" element={<RequestCollabPage />} />
                            <Route path="schedule-retail" element={<ScheduleRetailCollabPage />} />
                            <Route path="select-products" element={<SelectProductsPage />} />
                            <Route path="content-guidelines" element={<ContentGuidelinesPage />} />
                            <Route path="collab-details/:id" element={<CollabDetailsPage />} />
                            <Route path="marketplace-orders/:orderId" element={<MarketplaceOrderAcceptPage />} />
                            <Route path="marketplace-orders/:orderId/counter" element={<CounterMarketplaceOrderPage />} />
                            <Route path="marketplace-orders/:orderId/submission" element={<MarketplaceOrderSubmissionPage />} />
                            <Route path="marketplace-orders/:orderId/upload" element={<MarketplaceOrderUploadContentPage />} />
                            <Route path="/collabs/content-upload" element={<ContentUploadPage />} />
                            <Route path="pending-shipment" element={<PendingShipmentPage />} />
                          </Route>
                          <Route path="wallet" element={<Suspense fallback={<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>Loading wallet...</div>}><WalletPage /></Suspense>} />
                          <Route path="bounties" element={<Outlet />}>
                            <Route index element={<BountiesPage />} />
                            <Route path="propose" element={<ProposeBountyPage />} />
                            <Route path="proposals/:proposalId/resubmit" element={<ResubmitProposalPage />} />
                            <Route path="proposals/:proposalId/submitted" element={<ProposalSubmittedPage />} />
                            {/* claimed | instructions | submit | resubmit | submitted are steps of one page */}
                            <Route path="claims/:claimId/:step" element={<BountyClaimFlowPage />} />
                          </Route>
                          <Route path="inbox" element={<Outlet />}>
                            <Route index element={<Inbox />} />
                            <Route path="current" element={<Navigate to="/inbox" replace />} />
                            <Route path="previous" element={<Navigate to="/inbox" replace />} />
                            <Route path="unread" element={<Navigate to="/inbox" replace />} />
                          </Route>
                          <Route path="inbox/messages" element={<MessagesPage />} />
                          <Route path="inbox/messages/:conversationId" element={<MessagesPage />} />
                          <Route path="explore/*" element={
                              <Routes>
                                <Route path="/" element={<ExplorePage />} />
                                <Route path="client/:clientId">
                                  <Route index element={<ClientPage />} />
                                  <Route path="campaign/:campaignId" element={<CampaignDetailPage />} />
                                  <Route path="request-collab" element={<RequestCollabPage />} />
                                </Route>
                              </Routes>
                          } />
                          <Route path="*" element={<Navigate to="/login" replace />} />
                        </Route>
                      </>
                    )}
                  </Routes>
                  </ConversationsProvider>
                  </MarketplaceOrdersProvider>
                  </VerificationProvider>
                </InstagramVerificationProvider>
              </BountyDataProvider>
              </ClientDataProvider>
            </InfluencerCampaignDataProvider>
          </CollabDataProvider>
        </InfluencerLocationProvider>
      </InfluencerDataProvider>
    </>
  );
};

export default App;
